/**
 * Core Schema.org Business Identity Blocks
 * Consolidated foundational schemas with consistent NAP data and unified @id references
 */

// ── UNIFIED BUSINESS DATA ────────────────────────────────────────────────────
export const CORE_BUSINESS = {
  name: 'Rockhound Auto',
  legalName: 'Rockhound Auto LLC',
  alternateName: ['Rockhound Auto Sales', 'Oregon Coast Car Dealer', 'Warrenton Auto Broker'],
  url: 'https://rockhoundauto.com',
  
  // ✅ Phone — all formats
  telephone: '+18554686348',
  phone: '(855) 468-6348', // Display format
  phoneTel: 'tel:+18554686348', // href format
  phoneE164: '+18554686348', // E.164 international format
  
  // ✅ Email
  email: 'hello@rockhoundauto.com',
  
  // ✅ Address
  address: {
    streetAddress: '1425 SE 9th St',
    addressLocality: 'Warrenton',
    addressRegion: 'OR',
    postalCode: '97146',
    addressCountry: 'US'
  },
  
  // ✅ Geo Coordinates — AUTHORITATIVE (46.16075, -123.911)
  geo: {
    latitude: 46.16075,
    longitude: -123.911
  },
  
  // ✅ Maps URL
  mapsUrl: 'https://www.google.com/maps/place/Rockhound+Auto/@46.16075,-123.911,17z',
  
  // ✅ Hours
  hoursLabel: 'By Appointment Only',
  openingHours: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      description: 'By Appointment Only'
    }
  ],
  
  // ✅ Service Area
  areaServed: ['OR', 'WA', 'US'],
  
  // ✅ Social Media
  sameAs: [
    'https://www.google.com/maps/place/Rockhound+Auto/@46.16075,-123.911,17z',
    'https://www.facebook.com/rockhoundauto',
    'https://www.yelp.com/biz/rockhound-auto-warrenton',
    'https://www.instagram.com/rockhoundauto',
    'https://x.com/rockhoundauto',
    'https://www.tiktok.com/@rockhoundauto',
    'https://www.youtube.com/@rockhoundauto'
  ],
  
  logo: 'https://static.readdy.ai/image/b3efdc45e89a2660551e85a9fdfadec5/a59b3ad9d14210b4ce617fff8e7523fb.png',
  foundingDate: '2020-01-15',
  description: 'Rockhound Auto is a trusted used car dealership and auto broker in Warrenton, Oregon, serving the Oregon Coast and Southwest Washington. We offer transparent pricing, no-pressure sales, comprehensive vehicle inspections, and flexible financing for all credit situations.',
  slogan: 'No games. No pressure. Just honest cars.',
  priceRange: '$$',
  currenciesAccepted: 'USD',
  paymentAccepted: ['Cash', 'Credit Card', 'Debit Card', 'Bank Check', 'Wire Transfer', 'Auto Financing']
} as const;

// ── UNIFIED @ID REFERENCES ──────────────────────────────────────────────────
export const SCHEMA_IDS = {
  organization: `${CORE_BUSINESS.url}/#organization`,
  localBusiness: `${CORE_BUSINESS.url}/#localbusiness`, 
  automotiveBusiness: `${CORE_BUSINESS.url}/#automotivebusiness`,
  autoDealer: `${CORE_BUSINESS.url}/#autodealer`,
  website: `${CORE_BUSINESS.url}/#website`,
  breadcrumb: `${CORE_BUSINESS.url}/#breadcrumb`,
  contactPoint: `${CORE_BUSINESS.url}/#contactpoint`
} as const;

// ── SERVICE AREAS ────────────────────────────────────────────────────────────
export const SERVICE_AREAS = [
  // Oregon Cities
  { name: 'Warrenton', state: 'Oregon', stateCode: 'OR', zip: '97146', primary: true },
  { name: 'Astoria', state: 'Oregon', stateCode: 'OR', zip: '97103' },
  { name: 'Seaside', state: 'Oregon', stateCode: 'OR', zip: '97138' },
  { name: 'Cannon Beach', state: 'Oregon', stateCode: 'OR', zip: '97110' },
  { name: 'Tillamook', state: 'Oregon', stateCode: 'OR', zip: '97141' },
  { name: 'Lincoln City', state: 'Oregon', stateCode: 'OR', zip: '97367' },
  { name: 'Newport', state: 'Oregon', stateCode: 'OR', zip: '97365' },
  { name: 'Forest Grove', state: 'Oregon', stateCode: 'OR', zip: '97116' },
  { name: 'Hillsboro', state: 'Oregon', stateCode: 'OR', zip: '97123' },
  { name: 'Portland', state: 'Oregon', stateCode: 'OR', zip: '97201' },
  { name: 'Salem', state: 'Oregon', stateCode: 'OR', zip: '97301' },
  { name: 'Eugene', state: 'Oregon', stateCode: 'OR', zip: '97401' },
  { name: 'Bend', state: 'Oregon', stateCode: 'OR', zip: '97701' },
  // Washington Cities  
  { name: 'Longview', state: 'Washington', stateCode: 'WA', zip: '98632' },
  { name: 'Kelso', state: 'Washington', stateCode: 'WA', zip: '98626' },
  { name: 'Long Beach', state: 'Washington', stateCode: 'WA', zip: '98631' },
  { name: 'Vancouver', state: 'Washington', stateCode: 'WA', zip: '98660' },
  { name: 'Olympia', state: 'Washington', stateCode: 'WA', zip: '98501' },
  { name: 'Tacoma', state: 'Washington', stateCode: 'WA', zip: '98401' },
  { name: 'Seattle', state: 'Washington', stateCode: 'WA', zip: '98101' }
] as const;

// ── SOCIAL MEDIA & SAME AS ──────────────────────────────────────────────────
export const SOCIAL_PROFILES = CORE_BUSINESS.sameAs;

// ── OPENING HOURS ────────────────────────────────────────────────────────────
// Business operates by appointment only — no fixed open/close clock times
export const OPENING_HOURS = CORE_BUSINESS.openingHours;

// ── CORE SERVICES OFFERED ───────────────────────────────────────────────────
export const CORE_SERVICES = [
  {
    name: 'Used Car Sales',
    description: 'Quality pre-owned vehicles with comprehensive inspections, transparent pricing, and no hidden fees.'
  },
  {
    name: 'Auto Broker Services', 
    description: 'Professional vehicle sourcing and negotiation services to find any car from any dealership nationwide.'
  },
  {
    name: 'Auto Financing',
    description: 'Flexible financing options for all credit situations including bad credit, no credit, and first-time buyers.'
  },
  {
    name: 'Vehicle Trade-In',
    description: 'Fair market value trade-in appraisals with consistently higher offers than traditional dealerships.'
  },
  {
    name: 'Car Consignment',
    description: 'Professional vehicle consignment services to sell your car at the best possible price.'
  },
  {
    name: 'Vehicle Delivery',
    description: 'Complimentary delivery service throughout the Pacific Northwest for purchased vehicles.'
  }
] as const;

// ── CORE SCHEMA BUILDERS ────────────────────────────────────────────────────

/**
 * Build unified Organization schema with consistent data
 */
export function buildOrganizationSchema(additionalProps?: Record<string, unknown>): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': SCHEMA_IDS.organization,
    name: CORE_BUSINESS.name,
    legalName: CORE_BUSINESS.legalName,
    alternateName: CORE_BUSINESS.alternateName,
    url: CORE_BUSINESS.url,
    telephone: CORE_BUSINESS.telephone,
    email: CORE_BUSINESS.email,
    foundingDate: CORE_BUSINESS.foundingDate,
    description: CORE_BUSINESS.description,
    slogan: CORE_BUSINESS.slogan,
    logo: {
      '@type': 'ImageObject',
      url: CORE_BUSINESS.logo,
      width: 400,
      height: 120
    },
    image: CORE_BUSINESS.logo,
    address: {
      '@type': 'PostalAddress',
      ...CORE_BUSINESS.address
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: CORE_BUSINESS.geo.latitude,
      longitude: CORE_BUSINESS.geo.longitude
    },
    contactPoint: {
      '@type': 'ContactPoint',
      '@id': SCHEMA_IDS.contactPoint,
      telephone: CORE_BUSINESS.telephone,
      contactType: 'sales',
      areaServed: ['OR', 'WA', 'US'],
      availableLanguage: 'English'
    },
    sameAs: SOCIAL_PROFILES,
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      value: 5
    },
    knowsAbout: [
      'Used Cars',
      'Auto Brokering', 
      'Vehicle Financing',
      'Trade-In Appraisals',
      'Car Buying Services',
      'Oregon Coast Automotive',
      'Pacific Northwest Used Vehicles',
      'No-Pressure Car Sales'
    ],
    ...additionalProps
  };
}

/**
 * Build unified WebSite schema with SearchAction
 */
export function buildWebSiteSchema(additionalActions?: Array<Record<string, unknown>>): Record<string, unknown> {
  const defaultActions = [
    {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${CORE_BUSINESS.url}/inventory?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': SCHEMA_IDS.website,
    name: CORE_BUSINESS.name,
    url: CORE_BUSINESS.url,
    description: CORE_BUSINESS.description,
    publisher: {
      '@id': SCHEMA_IDS.organization
    },
    copyrightYear: new Date().getFullYear(),
    copyrightHolder: {
      '@id': SCHEMA_IDS.organization  
    },
    inLanguage: 'en-US',
    potentialAction: additionalActions ? [...defaultActions, ...additionalActions] : defaultActions,
    sameAs: SOCIAL_PROFILES,
    about: {
      '@id': SCHEMA_IDS.localBusiness
    }
  };
}

/**
 * Inject unified schema blocks into page head
 */
export function injectCoreSchemas(
  schemaBlocks: Record<string, Record<string, unknown>>,
  schemaId: string = 'core-schemas'
): () => void {
  // Remove existing schema if present
  const existing = document.getElementById(schemaId);
  if (existing) existing.remove();

  // Create new script tag with all schemas
  const script = document.createElement('script');
  script.id = schemaId;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(Object.values(schemaBlocks));
  document.head.appendChild(script);

  // Return cleanup function
  return () => {
    const el = document.getElementById(schemaId);
    if (el) el.remove();
  };
}

/**
 * ✅ Validation helper: Check if aggregateRating data is valid for schema inclusion
 * Returns true only if reviewCount > 0 and ratingValue is between 1-5
 */
export function isValidAggregateRating(rating?: { ratingValue: string | number; reviewCount: number }): boolean {
  if (!rating) return false;
  const numericRating = typeof rating.ratingValue === 'string' ? parseFloat(rating.ratingValue) : rating.ratingValue;
  return rating.reviewCount > 0 && numericRating >= 1 && numericRating <= 5;
}